/**
 * Price utility functions
 * Platform fee calculation must match app/Traits/PriceHelper.php
 * (calculateFeeUnitPrices / calculatePlatformFee).
 *
 * The fee model: the percentage part of the platform fee is rounded to whole
 * kronor PER PRICED UNIT (each passenger's ticket; each first-class seat's
 * extra; a bundle such as familjebiljett is one unit) and summed, plus one
 * fixed booking fee per booking. Because each displayed per-ticket price folds
 * in its own rounded percentage fee, the sum of the displayed prices plus the
 * booking fee is always exactly what is charged.
 */

export const roundToTwoDecimals = (number) => {
    return Math.round(number * 100) / 100;
};

// Platform defaults, used when a tenant has no fee settings configured.
// Percentage is a percent value (3 means 3%). Must match PriceHelper defaults.
export const DEFAULT_PERCENTAGE_FEE = 3.0;
export const DEFAULT_BOOKING_FEE_FIXED = 5.0;

/**
 * Resolve fee settings to concrete numbers, falling back to platform defaults.
 * `fees` is the tenant settings object: { percentage, fixed }.
 */
const resolveFees = (fees = {}) => ({
    rate: (fees.percentage != null ? fees.percentage : DEFAULT_PERCENTAGE_FEE) / 100,
    fixed: fees.fixed != null ? fees.fixed : DEFAULT_BOOKING_FEE_FIXED,
});

/**
 * Build the list of per-unit prices for the current booking: one entry per
 * passenger at their category price and one entry per first-class seat at the
 * first-class rate. Mirrors PriceHelper::calculateFeeUnitPrices.
 */
export const buildFeeUnitPrices = (travelers, station, seats, firstClassRate) => {
    const units = [];
    const categories = (travelers && travelers.categories) || {};
    const pricing = (station && station.pricing) || {};

    Object.keys(categories).forEach((categoryKey) => {
        const category = categories[categoryKey];
        if (!category || !(category.count > 0)) {
            return;
        }

        let unitPrice = null;
        const categoryPricing = pricing[categoryKey];
        if (categoryPricing && typeof categoryPricing.price === 'number') {
            unitPrice = categoryPricing.price;
        } else if (station) {
            // Fallback to legacy pricing fields if dynamic pricing not available
            const legacyPrice = station[`${categoryKey}Price`];
            if (typeof legacyPrice === 'number') {
                unitPrice = legacyPrice;
            }
        }
        if (unitPrice == null) {
            return;
        }

        for (let i = 0; i < category.count; i++) {
            units.push(unitPrice);
        }
    });

    (seats || []).forEach((seat) => {
        if (seat.firstClass && firstClassRate > 0) {
            units.push(firstClassRate);
        }
    });

    return units;
};

/**
 * Platform fee for a booking: percentage rounded to whole kronor per unit,
 * summed, plus the fixed booking fee. `discountRatio` scales every unit when a
 * coupon applies ((total - discount) / total), so the fee is taken on what is
 * actually paid. Must match PriceHelper::calculatePlatformFee.
 */
export const calculatePlatformFee = (unitPrices, fees = {}, discountRatio = 1) => {
    const { rate, fixed } = resolveFees(fees);
    const percentageFee = unitPrices.reduce(
        (sum, unitPrice) => sum + Math.round(unitPrice * discountRatio * rate),
        0
    );
    return roundToTwoDecimals(percentageFee + fixed);
};

/**
 * Total to pay for a booking: the (possibly discounted) ticket total plus the
 * per-unit platform fee.
 */
export const calculateTotalWithFee = (total, unitPrices, fees = {}, discountRatio = 1) => {
    return roundToTwoDecimals(total + calculatePlatformFee(unitPrices, fees, discountRatio));
};

/**
 * The fixed booking fee for the given tenant settings (the only fee shown separately).
 */
export const bookingFeeFixed = (fees = {}) => resolveFees(fees).fixed;

/**
 * A single unit's price with its (whole-krona rounded) percentage fee folded
 * in. Display only — does not change the stored ticket price or the value sent
 * to the backend for validation. Because the fee is rounded per unit, summing
 * these over a booking and adding the fixed booking fee gives exactly the
 * charged total.
 */
export const calculateTicketPriceWithPercentage = (price, fees = {}) => {
    const { rate } = resolveFees(fees);
    return roundToTwoDecimals(price + Math.round(price * rate));
};

/**
 * Displayed first-class price for a category: the category unit and the
 * first-class extra are separate fee units, so each folds in its own rounded
 * percentage fee.
 */
export const calculateFirstClassPriceWithPercentage = (price, firstClassRate, fees = {}) => {
    return roundToTwoDecimals(
        calculateTicketPriceWithPercentage(price, fees)
            + calculateTicketPriceWithPercentage(firstClassRate, fees)
    );
};
